import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import { green, grey } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";
import { addDays, format } from "date-fns";

import fpResourceTargetUtilizationService from "@plannel/services/fp/fp-resource-target-utilization-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup, { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import FpGridSetting, {
  readHide, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, buildDayBucketPivotColumnDefs, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap } from "@plannel/pages/factory-plan/common/chart";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — PlanResult 파일럿에서 실제 확인된 값(9)을
// 그대로 재사용한다(fp-report-screen-guide.md §A-3c). old ResourceTargetUtilization.jsx의 로컬
// fallback(14, `let fpSettings = { searchZone: 14 }`)이 아니라 그 실제 DB 값을 이식한다.
// TODO(MIG-T2): [resourcetargetutilization/ResourceTargetUtilization] 조회조건 세팅 3종(버킷 목록/
//   검색필드 표시/검색구간)의 테넌트별 커스터마이징 미이관.
//   old: search-option/bucket · search-option/srh-options · setting FP_SRH_RANGE를 각각 DB에서 조회
//   사유: 정적 구성(JSON 카탈로그/고정 카탈로그/상수)으로 전환 — 가이드 §A-3~§A-3d에서 결정
const SEARCH_ZONE_DAYS = 9;

// old SearchOptsGroup이 이 화면에서 실제로 쓰던 필드(loadData/loadChartData의 getValues 기준) —
// ResourceUtilization과 동일(같은 도메인, ResrcTargetUtilizationQueryRepository#makeWhereClause가
// stageCdNm/resourceCdNm 둘 다 contains 조건으로 소비).
const SEARCH_FIELD_LABELS = { stageCdNm: "FP_STAGE", resourceCdNm: "FP_RESOURCE" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const PARAM_DATE_FORMAT = "yyyyMMdd";

// old 백엔드 analysisIndicator 4종(ResourceTargetUtilizationService 상수) 고정 — 화면 고정 배열로
// 선언(백엔드가 항상 이 4개만 내려주므로 PlanResult의 itemCd처럼 클릭 시점에 동적으로 시리즈를
// 조립할 필요 없음). FP_UTILIZATION/FP_TARGET_UTILIZATION은 old처럼 선(line)+우측축("가동률 %"),
// FP_USED_TM/FP_AVAIL_TM은 막대(bar)+좌측축("시간 분") — old ResourceTargetUtilizationChart.jsx의
// rightYAxis 듀얼축 구성과 동일(JobChangeTimeInfo.js/MaterialPsi.js의 듀얼축 패턴 재사용).
const INDICATOR_ORDER = ["FP_USED_TM", "FP_AVAIL_TM", "FP_UTILIZATION", "FP_TARGET_UTILIZATION"];
// stackId: old INDICATOR_CONFIG의 `stack`을 그대로 이식 — 부하/가용 시간은 **서로 다른 스택**
// (old: 'bar1'/'bar2')이라 한 막대로 쌓이지 않고 나란히 그려진다. 부하 시간은 가용 시간의
// 부분집합(가동률 = 부하/가용)이라 둘을 누적하면 "부하+가용=전체"라는 잘못된 의미가 되고 가용
// 시간도 실제보다 커 보인다 — old가 스택을 나눈 이유이고, 이관 시 둘 다 'bar1'로 묶어버린 것을
// 2026-08-12 사용자 지적으로 바로잡았다.
const INDICATOR_CONFIG = {
  FP_USED_TM: { type: "bar", orientation: "left", color: "#4472C4", stackId: "bar1" },
  FP_AVAIL_TM: { type: "bar", orientation: "left", color: "#ED7D31", stackId: "bar2" },
  FP_UTILIZATION: { type: "line", orientation: "right", color: "#70AD47" },
  FP_TARGET_UTILIZATION: { type: "line", orientation: "right", color: "#FFC950" }
};
const RATE_INDICATORS = new Set(["FP_UTILIZATION", "FP_TARGET_UTILIZATION"]);

// 그리드 셀과 차트(y축 눈금·툴팁)가 **같은 함수**를 쓰도록 여기 한 번만 정의한다 — 이관 당시
// 그리드에만 포맷을 걸고 차트는 공용 기본값(numberUtil.formatNumber)에 맡겨서 비율 축에만 '%'가
// 빠져 있었다(2026-08-12 사용자 지적). 차트 쪽은 PivotChartComposed의 y축별 포맷터 확장 포인트
// (chartSeries[].formatter)로 넘긴다 — DP DpPlanVsActual/RP RpPlanVsActual/ShippingAlert 등
// 다른 모듈이 이미 쓰는 방식이다.
const formatRate = (value) => `${Number(value).toFixed(3)}%`;
const formatMinutes = (value) => Number(value).toLocaleString();

// 행 그룹(트리) 계층 — 공장 > 스테이지 > 자원(leaf는 analysisIndicator 4종).
// old가 실제로 rowGroup을 건 건 stageCd 하나뿐이고(`gridView.groupBy(['stageCd'])`) 공장/자원은
// `mergeRule` 세로 병합이라, 이관 초기엔 old 그대로 stageCd만 그룹 + 공장/자원은 spanRows로
// 재현했다. 2026-08-12 사용자 지시로 **다른 Report 화면(ResourceUtilization/JobChangeTimeInfo
// 등 공장>스테이지>자원 3단)과 통일**하기 위해 공장/자원도 행 그룹으로 올린다 — 데이터 그레인은
// analysisIndicator(자원당 4행)라 공장/자원을 그룹으로 올려도 §A-6b "그레인까지 걸지 않는다"
// 기준에 어긋나지 않는다(그레인은 여전히 leaf).
const GROUP_FIELDS = ["plantCd", "plantNm", "stageCd", "stageNm", "resourceCd", "resourceNm"];

const STAGE_CD_COL_ID = "stageCdGrp";
const STAGE_NM_COL_ID = `STAGE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const STAGE_NM_HIDE_KEY = "stageNmColsHide";

const PLANT_CD_COL_ID = "plantCdGrp";
const PLANT_NM_COL_ID = `PLANT_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const PLANT_NM_HIDE_KEY = "plantNmColsHide";
const RESOURCE_CD_COL_ID = "resourceCdGrp";
const RESOURCE_NM_COL_ID = `RESOURCE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const RESOURCE_NM_HIDE_KEY = "resourceNmColsHide";

// 그룹(접힌 stageCd 대표 행)·푸터·상단 pinned 요약 행 — 전부 aggFunc(그룹 전체 가동률 재계산)로
// 값이 채워지는 "집계 행"이다. 이 셋을 한데 판단해서 %/굵게 스타일을 공유한다.
const isAggregateRow = (params) => (
  params.node?.rowPinned === "top" || params.node?.footer || params.node?.group
);
const isRateRow = (params) => isAggregateRow(params) || RATE_INDICATORS.has(params.data?.analysisIndicator);

// old columnProperty.groupFooter.valueCallback을 AG Grid aggFunc로 재현 — 그룹(stageCd) 안의
// FP_USED_TM/FP_AVAIL_TM 두 analysisIndicator 행을 합산해 (usedTm/availTm)*100을 계산한다.
// AG Grid 기본 aggFunc(sum 등)는 이 컬럼의 값만 보고, 형제 행의 analysisIndicator를 구분해서
// 골라 합산하는 건 지원하지 않아 함수형 aggFunc로 직접 구현한다(rowNode.allLeafChildren로 그룹
// 전체 leaf 행의 원본 data에 접근). 같은 함수가 그룹 대표 행(접힌 상태)·그룹 푸터(펼친 상태) 양쪽에
// 쓰인다 — AG Grid가 aggFunc 결과를 두 자리 모두에 채워준다.
const stageUtilizationAggFunc = (params) => {
  const field = params.column.getColId();
  let usedTm = 0;
  let availTm = 0;

  (params.rowNode?.allLeafChildren ?? []).forEach((node) => {
    const data = node.data;
    if (!data) return;
    if (data.analysisIndicator === "FP_USED_TM") usedTm += data[field] ?? 0;
    else if (data.analysisIndicator === "FP_AVAIL_TM") availTm += data[field] ?? 0;
  });

  return usedTm && availTm ? (usedTm / availTm) * 100 : null;
};

// old columnProperty.valueCallback(0 이하 값은 빈 셀) — 그리드 전용, 차트에는 적용하지 않는다.
const suppressNonPositive = (value) => (value === null || value === undefined || value <= 0 ? null : value);

const ResourceTargetUtilization = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });
  // stageCdNm/resourceCdNm — Report 공통 정책(§A-9b)대로 조회 버튼이 없다. 자유 텍스트는
  // SearchOptsGroupRetrieve(TextFieldSearch 기반) 안의 Enter/검색아이콘으로만 커밋한다.
  const stageCdNmRef = useRef("");
  const resourceCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  // 그리드 상단 headerSummary(전체 목표 가동률) 대응 — pinnedTopRowData 1행.
  const [topRowData, setTopRowData] = useState([]);

  const [chartData, setChartData] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  const prevChartParamsRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const searchStateRef = useRef({ versionState, periodState });
  searchStateRef.current = { versionState, periodState };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveResourceTargetUtilization(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      // old groupFooter 행(스테이지별 평균 가동률)은 배경색으로 "계산된 행"임을 표시했다 —
      // old와 동일하게 회색을 쓴다. green[50]은 아래 "가동률 지표 행" 강조에 배정했으므로(2026-08-12
      // 사용자 지시) 두 강조가 같은 색이 되지 않도록 여기서 색을 나눈다. 읽기 전용 화면이라
      // 공용 DataState.getRowStyles(편집 상태 표시)는 쓰지 않는다.
      getRowStyle: (params) => (params.node?.footer ? { backgroundColor: grey[200], fontWeight: "bold" } : null),
      enableCellSpan: true,
      ...REPORT_TREE_GRID_OPTIONS,
      // old groupFooter(setCrossTabGridLayoutAndOptions) — stageCd 그룹이 **펼쳐진 상태에서도**
      // 그룹 맨 아래에 스테이지 가동률 집계 행을 보여준다.
      // ⚠️ 옵션명 주의: `groupIncludeFooter`는 AG Grid 31에서 `groupTotalRow`로 이름이 바뀐 구버전
      // 옵션이라 35에서는 조용히 무시된다 — 그래서 펼친 상태엔 아무것도 안 나오고 그룹을 접었을
      // 때만 대표 행에 집계값이 보이는(= aggFunc 기본 동작) 상태였다(2026-08-12 사용자 지적으로 발견).
      // ⚠️ 문자열("bottom")로 주면 **모든 그룹 레벨**(공장/스테이지/자원)에 푸터가 생긴다 —
      // old의 합계 행은 스테이지 단위 하나뿐이므로 콜백형(UseGroupTotalRow)으로 스테이지 레벨만
      // 고른다(공장/자원까지 행 그룹으로 올린 2026-08-12 변경에 따른 후속 처리).
      groupTotalRow: (params) => (params.node?.field === "stageCd" ? "bottom" : undefined),
      // 피벗 컬럼에 aggFunc(stageUtilizationAggFunc)을 걸면 AG Grid가 행 그룹 활성 상태에서 헤더에
      // 집계함수 이름을 자동으로 덧붙인다 — 내장 함수면 "sum(2026-08-14)", 이 화면처럼 이름 없는
      // 함수형 aggFunc면 "func(2026-08-14)"가 된다(2026-08-12 사용자 지적으로 발견). old엔 없던
      // 표기이고 날짜/요일 헤더를 가리므로 끈다.
      suppressAggFuncInHeader: true,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 분석 지표(leaf) 컬럼 — 그룹/푸터/pinned 요약 행일 때는 old headerSummary/groupFooter 라벨을
  // 대신 보여준다(old plantCd 컬럼의 headerSummary.text/groupFooter.valueCallback 라벨을 이
  // 컬럼으로 옮긴 것 — plantCd는 rowGroup이 아니라 라벨을 얹을 자리로 쓰기엔 맞지 않아, old에서도
  // 실제 데이터 구분자였던 analysisIndicator 컬럼에 통일).
  const analysisIndicatorFormatter = (params) => {
    if (params.node?.rowPinned === "top") return t("FP_TOTAL_TARGET_UTILIZATION");
    // old groupFooter 라벨 "<스테이지코드> 가동률"(예: "STAGE#BUSAN 가동률") 재현 — 푸터 노드의
    // node.key가 그룹 키(stageCd)다. 접힌 그룹 대표 행은 스테이지 코드 컬럼에 이미 코드가 보이므로
    // old처럼 지표명만 둔다.
    if (params.node?.footer) return `${params.node.key ?? ""} ${t("FP_UTILIZATION")}`.trim();
    if (params.node?.group) return t("FP_UTILIZATION");
    return t(params.value);
  };
  // 가동률(FP_UTILIZATION) 지표 행 강조 — 분석 지표 컬럼부터 오른쪽 피벗 컬럼까지 green[50]
  // 배경(2026-08-12 사용자 지시로 "굵게" 대신 배경색으로 변경, 다른 Report 화면의 지표 행 강조
  // 관례와 동일). 집계 행(그룹 푸터/그룹 대표/상단 pinned)은 배경을 주지 않고 굵게만 유지한다 —
  // 푸터의 회색 배경(getRowStyle)을 셀 배경이 덮어쓰지 않도록.
  const utilizationCellStyle = (params) => {
    if (isAggregateRow(params)) return { fontWeight: "bold" };
    return params.data?.analysisIndicator === "FP_UTILIZATION" ? { backgroundColor: green[50] } : null;
  };
  const analysisIndicatorCellStyle = utilizationCellStyle;

  // old columnProperty.styleCallback(피벗 셀) — FP_UTILIZATION/FP_TARGET_UTILIZATION은 suffix '%',
  // FP_UTILIZATION만 굵게. 그룹 대표 행(접힌 상태)·그룹 푸터·pinned 요약 행은 전부 aggFunc로 계산된
  // 가동률 값이라 같은 취급(isRateRow/isUtilizationRow).
  const pivotValueFormatter = (params) => {
    if (params.value === null || params.value === undefined || params.value === "") return "";
    return isRateRow(params) ? formatRate(params.value) : formatMinutes(params.value);
  };
  const pivotCellStyle = utilizationCellStyle;

  // toolResourceYn — old mergeRule(세로 병합) 대응. 값이 같은 인접 행만 병합한다(그룹 행은 대상
  // 아님). 공장/스테이지/자원은 행 그룹으로 올라가면서 spanRows가 필요 없어졌고, 이 컬럼만 남았다.
  // ⚠️ nodeA/nodeB는 AG Grid 타입 계약상 nullable(SpanRowsParams: `IRowNode | null`)이다 —
  // 첫 조회 시 경계 행을 비교하며 null이 들어와 TypeError로 화면이 죽은 사례가 있다(2026-08-20).
  // 널 가드를 먼저 두고, data가 없는 그룹/푸터 행도 병합 대상에서 제외한다.
  const spanByField = (field) => ({ nodeA, nodeB }) => (
    !!nodeA?.data && !!nodeB?.data && !nodeA.group && !nodeB.group &&
    nodeA.data[field] === nodeB.data[field]
  );

  // 그룹 표시 컬럼 3쌍 공통 — 그룹 푸터의 "Total <그룹키>" 기본 라벨 제거(위 주석 참고).
  const withEmptyTotalLabel = (def) => ({
    ...def,
    cellRendererParams: { ...def.cellRendererParams, totalValueGetter: () => "" }
  });

  const fixedColumnDefs = useMemo(() => [
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),

    // 공장/스테이지/자원 3단 전부 rowGroup 표시 컬럼(showRowGroup, §A-6b 표준 패턴) —
    // ResourceUtilization.js 등 다른 Report 화면과 동일한 구성.
    // 그룹 푸터 행에서 AG Grid가 기본으로 넣는 "Total <그룹키>"(예: "Total ASSEMBLY") 라벨은
    // old에 없다(old는 이 칸을 비우고 분석 지표 칸에만 "<코드> 가동률"을 넣는다) — 그룹 셀
    // 렌더러의 totalValueGetter로 빈 문자열을 돌려 지운다(세 쌍 모두 동일).
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, PLANT_NM_HIDE_KEY),
      cdColId: PLANT_CD_COL_ID, cdField: "plantCd", cdHeader: "FP_PLANT_CD",
      nmColId: PLANT_NM_COL_ID, nmField: "plantNm", nmHeader: "PLANT_NM",
      hideKey: PLANT_NM_HIDE_KEY, width: 150
    }).map(withEmptyTotalLabel),

    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, STAGE_NM_HIDE_KEY),
      cdColId: STAGE_CD_COL_ID, cdField: "stageCd", cdHeader: "FP_STAGE_CD",
      nmColId: STAGE_NM_COL_ID, nmField: "stageNm", nmHeader: "FP_STAGE_NM",
      hideKey: STAGE_NM_HIDE_KEY, width: 150
    }).map(withEmptyTotalLabel),

    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, RESOURCE_NM_HIDE_KEY),
      cdColId: RESOURCE_CD_COL_ID, cdField: "resourceCd", cdHeader: "FP_RESOURCE_CD",
      nmColId: RESOURCE_NM_COL_ID, nmField: "resourceNm", nmHeader: "FP_RESOURCE_NM",
      hideKey: RESOURCE_NM_HIDE_KEY, width: 150
    }).map(withEmptyTotalLabel),

    // toolResourceYn — old dataType:"boolean" + mergeRule(자원 단위 세로 병합), ResourceUtilization.js와 동일 기법.
    // 공용 booleanColumn 렌더러는 pinned 행만 걸러내고 그룹 푸터/그룹 행에는 체크박스를 그려서,
    // 데이터가 없는 집계 행에 "해제된 체크박스"가 보인다(old엔 빈 칸) — 집계 행이면 렌더러를 비운다.
    {
      headerName: "FP_TOOL_RESOURCE_YN", field: "toolResourceYn", colId: "toolResourceYn", width: 130,
      pinned: "left", lockPinned: true, type: ["booleanColumn", "centerAligned"],
      spanRows: spanByField("resourceCd"),
      cellRendererSelector: (params) => (isAggregateRow(params) ? { component: () => null } : undefined)
    },

    // leaf(분석 지표) — old displayCallback가 항상 transLangKey(value)로 번역, FP_UTILIZATION만 굵게.
    {
      headerName: "FP_ANALYSIS_INDICATOR", field: "analysisIndicator", colId: "analysisIndicator", width: 150,
      pinned: "left", lockPinned: true, type: ["nonEditableColumn"],
      valueFormatter: analysisIndicatorFormatter, cellStyle: analysisIndicatorCellStyle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // 이 화면의 백엔드(ResourceTargetUtilizationService#getResourceTargetUtilization)는 시간
  // 버킷이면 빈 결과를 반환한다(`Bucket.NULL.equals(bucket) || bucket.isHourBucket()`) —
  // ResrcUtilization/JobChangeTimeInfo와 같은 이유로 이 화면도 BUCKET_CASE.WITHOUT_HOUR를 쓴다
  // (fp-report-screen-guide.md §A-3 배정표에 이 화면 추가 필요).
  const buildPivotColumnDefs = (crosstabHeader) => buildDayBucketPivotColumnDefs(crosstabHeader, t, {
    childExtraProps: () => ({
      valueFormatter: pivotValueFormatter, cellStyle: pivotCellStyle, aggFunc: stageUtilizationAggFunc
    })
  });

  // old setCrossTabGridDataWithBucket — pivotData[i]를 crosstabHeader[i] 필드로 펼친다.
  // valueCallback(0 이하 → null)도 여기서 함께 적용(그리드 전용).
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {
      plantCd: row.plantCd, plantNm: row.plantNm,
      stageCd: row.stageCd, stageNm: row.stageNm,
      resourceCd: row.resourceCd, resourceNm: row.resourceNm,
      toolResourceYn: row.toolResourceYn,
      analysisIndicator: row.analysisIndicator
    };
    (crosstabHeader ?? []).forEach((header, index) => {
      flat[header] = suppressNonPositive(row.pivotData?.[index]);
    });
    return flat;
  });

  // old headerSummary.valueCallback(top.pivotData) — 응답의 top(단일 객체, analysisIndicator=
  // FP_TARGET_UTILIZATION)을 crosstabHeader와 같은 규칙으로 펼쳐 pinnedTopRowData 1행을 만든다.
  const buildTopRow = (crosstabHeader, top) => {
    if (!top) return [];
    const row = { analysisIndicator: top.analysisIndicator ?? "FP_TARGET_UTILIZATION" };
    (crosstabHeader ?? []).forEach((header, index) => { row[header] = top.pivotData?.[index]; });
    return [row];
  };

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const buildBaseParams = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      stageCdNm: stageCdNmRef.current,
      resourceCdNm: resourceCdNmRef.current,
      bucketType: period.bucketType,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartData([]);
    setChartLabels([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 브레드크럼 — ResourceUtilization.js와 동일 슬롯 매핑(공장→originLoc, 스테이지→
  // destLoc, 자원→item).
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      originLocCd: target.plantCd ?? null, originLocCdHeader: t("FP_PLANT_CD"),
      originLocNm: target.plantNm ?? null, originLocNmHeader: t("PLANT_NM"),
      destLocCd: target.stageCd ?? null, destLocCdHeader: t("FP_STAGE_CD"),
      destLocNm: target.stageNm ?? null, destLocNmHeader: t("FP_STAGE_NM"),
      itemCd: target.resourceCd ?? null, itemCdHeader: t("FP_RESOURCE_CD"),
      itemNm: target.resourceNm ?? null, itemNmHeader: t("FP_RESOURCE_NM")
    });
  };

  // 클릭 기준으로 차트를 다시 조회한다. old updateChartFromGrid와 동일 — 상위 레벨을 클릭하면
  // 하위 코드는 비워서 보낸다(백엔드가 useGrouping:true일 때 이 레벨로 집계, GroupingLevel 참고).
  const loadChart = (target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;

    updateStatusBreadcrumb(target);

    const nextParams = {
      plantCd: target.plantCd ?? "", stageCd: target.stageCd ?? "", resourceCd: target.resourceCd ?? ""
    };
    const prev = prevChartParamsRef.current;
    if (prev && prev.plantCd === nextParams.plantCd && prev.stageCd === nextParams.stageCd
      && prev.resourceCd === nextParams.resourceCd) {
      return;
    }
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    const params = { ...buildBaseParams(), ...nextParams, useGrouping: true };

    // TODO(MIG-T2): [resourcetargetutilization/ResourceTargetUtilization] 그리드 컬럼 필터(값 목록
    //   체크박스) 미이관.
    //   old: plantCd/stageCd/resourceCd/toolResourceYn/analysisIndicator에 RealGrid autoFilter,
    //        필터 선택값을 차트 조회의 changes(FilterParameter)로 함께 전송
    //   사유: stageCd가 rowGroup+hide:true인 숨은 데이터 컬럼이 되면서 Set Filter 트리거 UI를 둘
    //        자리가 없어졌다. 백엔드는 changes를 계속 받으므로 2차에 트리거 UI만 얹으면 된다
    //        (가이드 §A-6e와 동일한 결정) — 지금은 빈 필터로 보낸다.
    fpResourceTargetUtilizationService.getResrcTargetUtilizationChart(params, {})
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { chartHeader, data } = response.data ?? {};
        const rows = data ?? [];

        if (isEmpty(rows) || isEmpty(chartHeader)) {
          setChartData([]);
          setChartLabels([]);
          return;
        }

        setChartLabels(chartHeader);
        setChartData(rows.map(r => {
          const point = { analysisIndicator: r.analysisIndicator };
          chartHeader.forEach((header, index) => { point[header] = r.pivotData?.[index] ?? 0; });
          return point;
        }));
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrieveResourceTargetUtilization = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    const params = { ...buildBaseParams(), plantCds: version.plantCd, useGrouping: false };

    gridRef.current?.api?.setGridOption("loading", true);

    fpResourceTargetUtilizationService.getResrcTargetUtilization(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data, top } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);
        setTopRowData(buildTopRow(crosstabHeader, top));

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        // old: 조회 직후 첫 행의 공장 기준으로 차트를 그리고, 그리드의 선택 셀도 그 행으로 옮긴다.
        // ⚠️ colId는 **필드명(`plantCd`)이 아니라 컬럼 colId(`PLANT_CD_COL_ID` = "plantCdGrp")** 다 —
        // 행 그룹 표시 컬럼은 `buildGroupColumnPair`가 field와 다른 colId를 붙인다(§A-7). 필드명을
        // 넣으면 `consumePendingFocus`의 addCellRange/setFocusedCell이 없는 컬럼을 가리켜 **조용히
        // 아무 일도 안 하고**, 차트만 그려지고 그리드 선택 표시가 안 되는 증상이 된다(2026-08-14 수정).
        pendingFocusRef.current = { rowIndex: 0, colId: PLANT_CD_COL_ID };
        prevChartParamsRef.current = null;
        loadChart({ plantCd: rows[0].plantCd, plantNm: rows[0].plantNm });
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  const STAGE_LEVEL_COL_IDS = new Set([STAGE_CD_COL_ID, STAGE_NM_COL_ID]);
  const PLANT_LEVEL_COL_IDS = new Set([PLANT_CD_COL_ID, PLANT_NM_COL_ID]);

  // 클릭한 셀에서 차트를 그릴 드릴다운 대상을 뽑는다(old updateChartFromGrid의
  // `column.startsWith('plant'|'stage')` 분기를 트리 표시 컬럼 colId 기준으로 옮긴 것).
  // 그룹 행(접힌 대표 행/푸터)은 그 그룹의 첫 leaf에서 값을 보충하되, **그 그룹 레벨까지만** 채운다
  // — 공장/자원까지 행 그룹이 된 뒤로는 어느 레벨의 그룹 행인지(node.field)에 따라 하위 코드를
  // 비워야 백엔드가 그 레벨로 집계한다(useGrouping:true, GroupingLevel).
  const resolveChartTarget = (params) => {
    const node = params.node;
    if (!node) return null;

    if (node.group) {
      const leaf = node.allLeafChildren?.[0]?.data;
      if (!leaf) return null;

      const target = { plantCd: leaf.plantCd, plantNm: leaf.plantNm };
      if (node.field === "plantCd" || node.field === "plantNm") return target;

      target.stageCd = leaf.stageCd;
      target.stageNm = leaf.stageNm;
      if (node.field === "stageCd" || node.field === "stageNm") return target;

      target.resourceCd = leaf.resourceCd;
      target.resourceNm = leaf.resourceNm;
      return target;
    }

    const row = node.data;
    if (!row) return null;

    const colId = params.column?.getColId() ?? "";
    if (PLANT_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm };
    }
    if (STAGE_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm };
    }
    return {
      plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm,
      resourceCd: row.resourceCd, resourceNm: row.resourceNm
    };
  };

  const onCellClicked = (params) => {
    const target = resolveChartTarget(params);
    if (target) loadChart(target);
  };

  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  // 버전이 바뀌면 조회 구간을 다시 계산한다(old setVersionPeriod) — 다른 Report 화면과 동일 원리.
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = addDays(startDate, SEARCH_ZONE_DAYS);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    setTopRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 선택형 조건(버전/plant/기간/버킷)이 바뀌면 값이 준비되는 즉시 자동 조회한다(§A-9b).
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodState.startDate, periodState.endDate, periodState.bucketType, periodState.isEntirePlanPeriod,
    versionState.plantCd
  ]);

  // old ResourceTargetUtilizationChart.jsx는 ChartLegendPlugin을 plugins 배열에 실제로 포함시키지
  // 않았다(`plugins={[ChartAxisTitlePlugin]}`, PlanResult와 같은 케이스 — §A-7 "판단 시 주의" 참고,
  // old 소스의 plugins 배열을 직접 확인함). → recharts 기본 <Legend/>를 화면 스코프 CSS로 숨긴다.
  const leftAxisLabel = useMemo(() => `(${t("TIME")}: ${t("FP_MINUTES")})`, [t]);
  const rightAxisLabel = useMemo(() => `(${t("FP_UTILIZATION")}: %)`, [t]);

  // TODO(MIG-T2): [resourcetargetutilization/ResourceTargetUtilization] 값이 0/없는 지표 시리즈
  //   자동숨김 미이관.
  //   old: setChart의 hasValue 체크(FP_UTILIZATION류는 항상 표시, 나머지는 값이 전부 0/null이면
  //        해당 시리즈 자체를 datasets에서 제외)
  //   사유: 화면 고정 4개 지표라 old도 실사용 시 거의 항상 4개가 다 나와 체감 차이가 적고, 1차
  //        목표(조회·표시)에 불필요 — MaterialPsi.js와 동일 결정(deferred-features.md 등재)
  const pivotConfig = useMemo(() => ({
    groupKey: "analysisIndicator",
    chartSeries: INDICATOR_ORDER.map(indicator => {
      const conf = INDICATOR_CONFIG[indicator];
      const isLine = conf.type === "line";
      const isRate = RATE_INDICATORS.has(indicator);
      const name = isRate ? `${t(indicator)} (%)` : t(indicator);
      return {
        field: indicator, text: indicator, type: conf.type,
        groupName: isLine ? rightAxisLabel : leftAxisLabel,
        orientation: conf.orientation,
        // y축 눈금·툴팁 포맷을 그리드 셀과 동일하게 맞춘다(위 formatRate/formatMinutes 주석 참고).
        // PivotChartComposed는 이 formatter를 groupName(=y축) 단위로 모아 쓰므로, 같은 축에 묶이는
        // 시리즈는 같은 포맷터를 갖는다(비율 2종=우측축, 시간 2종=좌측축).
        formatter: isRate ? formatRate : formatMinutes,
        typeOptions: isLine ? { stroke: conf.color, name } : { fill: conf.color, stackId: conf.stackId, name }
      };
    }),
    include: chartLabels,
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [chartLabels, leftAxisLabel, rightAxisLabel, t]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          // 이 화면의 백엔드는 시간 버킷이면 빈 결과를 반환한다 — ResrcUtilization/JobChangeTimeInfo와
          // 같은 이유로 WITHOUT_HOUR(fp-report-screen-guide.md §A-3 배정표에 이 화면 추가 필요).
          bucketCase={BUCKET_CASE.WITHOUT_HOUR}
        />
        <SearchOptsGroupRetrieve
          fieldRefs={{ stageCdNm: stageCdNmRef, resourceCdNm: resourceCdNmRef }}
          fieldKeys={SEARCH_FIELD_KEYS}
          labels={SEARCH_FIELD_LABELS}
          retrieve={retrieveResourceTargetUtilization}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      {/* old VerticalGridChartBox의 sizes={[50, 50]}(direction="vertical") 대응(§A-1c). */}
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-resource-target-utilization-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            pinnedTopRowData={topRowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* old ResourceTargetUtilizationChart.jsx는 `plugins={[ChartLegendPlugin, ChartAxisTitlePlugin]}`로
              범례 플러그인을 **실제로 등록**했다 — 즉 old에도 범례가 있었다(PlanResult처럼 "옵션은
              있지만 plugins 배열엔 없어 렌더링되지 않던" 케이스가 아니다, §A-7 "판단 시 주의").
              이관 당시 recharts 기본 <Legend/>를 CSS로 숨겨뒀던 건 오판이라 2026-08-12 되돌린다. */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              // decimalPoint:true — PivotChartComposed(공용)는 기본적으로 차트 데이터 자체를
              // Math.round()로 정수 반올림한다(PivotChartComposed.js:245-253). 가동률(소수 3자리)이
              // 정수로 뭉개져 그리드와 값이 달라 보이던 원인이라 반올림을 끈다 — 포맷터(formatRate)는
              // 이미 뭉개진 값을 받으므로 포맷터만으로는 해결되지 않는다(2026-08-14 확인).
              // ItemCycleTime.js/IP InventoryOverview/RP RpMonthlyTrend 등 소수가 필요한 다른
              // 차트들이 쓰는 것과 동일한 옵션이다.
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ResourceTargetUtilization);
